import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { fetchAnlagen, fetchKomponenten, type Komponente } from "@/lib/api"
import { Badge, Input, Select } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import { Sheet, SheetContent, SHEET_WIDTH } from "@/components/Sheet"
import { KomponenteDetailContent } from "./KomponenteDetailPage"

/**
 * Globale, filterbare Komponenten-Liste über alle Anlagen (P3 aus
 * MASCHINEN_STRUKTURVERGLEICH.md) — ersetzt den bisherigen stummen
 * Catch-all-Redirect auf /dashboard. 829 Komponenten sind laut Befund der
 * eigentliche Arbeitsbestand der Techniker.
 *
 * KARTEN_FAEHIGKEIT_AUDIT.md 🟡 → Dual-Muster: Zeilen-Klick öffnet
 * KomponenteDetailContent in einer breiten resizable Karte (SHEET_WIDTH.xl);
 * `/maschinen/komponenten/:id` bleibt als Deep-Link-Fallback (KomponenteDetailPage).
 */
export function KomponentenPage() {
  const [suche, setSuche] = useState("")
  const [anlageFilter, setAnlageFilter] = useState<string>("")
  const [detailKomponenteId, setDetailKomponenteId] = useState<number | null>(null)

  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })
  const { data: komponenten, isLoading, isError, refetch } = useQuery({
    queryKey: ["komponenten", "alle"],
    queryFn: () => fetchKomponenten(),
  })

  const anlageName = (id: number) => anlagen?.find((a) => a.id === id)?.bezeichnung ?? `Anlage #${id}`

  // Die Suche deckt auch den Navision-Code ab, der keine Grid-Spalte ist —
  // deshalb bleibt sie zusätzlich zum Schnellfilter des Grids erhalten.
  const gefiltert = useMemo(() => {
    let liste = komponenten ?? []
    if (anlageFilter) liste = liste.filter((k) => k.anlageId === Number(anlageFilter))
    if (suche.trim()) {
      const q = suche.trim().toLowerCase()
      liste = liste.filter(
        (k) =>
          k.beschreibung.toLowerCase().includes(q) ||
          (k.tagNummer ?? "").toLowerCase().includes(q) ||
          (k.navisionCode ?? "").toLowerCase().includes(q),
      )
    }
    return liste
  }, [komponenten, anlageFilter, suche])

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Komponente>[]>(
    () => [
      {
        id: "beschreibung",
        header: "Beschreibung",
        accessorFn: (k) => k.beschreibung,
        cell: ({ row }) => row.original.beschreibung,
        meta: { cellClassName: "text-slate-900" },
      },
      {
        id: "tagNummer",
        header: "TAG-Nummer",
        accessorFn: (k) => k.tagNummer ?? "",
        cell: ({ row }) => row.original.tagNummer ?? "—",
        meta: { cellClassName: "font-mono text-xs" },
      },
      {
        id: "anlage",
        header: "Anlage",
        accessorFn: (k) => anlageName(k.anlageId),
        cell: ({ row }) => (
          <Link
            to={`/maschinen/anlagen/${row.original.anlageId}`}
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {anlageName(row.original.anlageId)}
          </Link>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (k) => (k.gesperrt ? "gesperrt" : "frei"),
        cell: ({ row }) => (
          <Badge tone={row.original.gesperrt ? "red" : "green"}>
            {row.original.gesperrt ? "gesperrt" : "frei"}
          </Badge>
        ),
      },
    ],
    // anlageName hängt an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [anlagen],
  )

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Komponenten</h1>
        <p className="mt-1 text-sm text-slate-600">
          Alle Komponenten über alle Anlagen — Suche und Filter nach Anlage.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input
          type="text"
          placeholder="Suche nach Beschreibung/TAG-Nummer/Navision-Code…"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          className="w-80"
        />
        <Select
          value={anlageFilter}
          onChange={(e) => setAnlageFilter(e.target.value)}
          className="w-auto"
        >
          <option value="">Alle Anlagen</option>
          {anlagen?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.bezeichnung}
            </option>
          ))}
        </Select>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Komponenten …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Komponenten konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {komponenten && (
        <DataGrid
          columns={spalten}
          data={gefiltert}
          storageKey="komponenten"
          onRowClick={(k) => setDetailKomponenteId(k.id)}
          emptyMessage={
            suche || anlageFilter
              ? "Keine Komponenten für diese Filter gefunden."
              : "Noch keine Komponenten erfasst."
          }
        />
      )}

      {/* Detail-Sheet von rechts (UX_KONZEPT.md §7, KARTEN_FAEHIGKEIT_AUDIT.md 🟡)
          — breite Karte mit Dual-Muster; `/maschinen/komponenten/:id` bleibt
          als Deep-Link-Fallback (KomponenteDetailPage). */}
      <Sheet open={detailKomponenteId !== null} onOpenChange={(open) => !open && setDetailKomponenteId(null)}>
        <SheetContent className="overflow-y-auto" sheetKey="komponente-detail" defaultWidth={SHEET_WIDTH.xl}>
          {detailKomponenteId !== null && <KomponenteDetailContent komponenteId={detailKomponenteId} />}
        </SheetContent>
      </Sheet>
    </div>
  )
}
